import { useEffect, useRef, useState } from 'react';
import { FaMicrophone, FaStop } from 'react-icons/fa';

interface VoiceSearchProps {
  onResult: (text: string) => void;
  isActive: boolean;
  onListeningChange: (listening: boolean) => void;
}

const VoiceSearch = ({ onResult, isActive, onListeningChange }: VoiceSearchProps) => {
  const recognitionRef = useRef<any>(null);
  const [isSupported, setIsSupported] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    // Check if browser supports Web Speech API
    // @ts-ignore
    const SpeechRecognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setError('');
      onListeningChange(true);
    };

    recognition.onresult = (event: any) => {
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;

        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        }
      }

      if (finalTranscript) {
        onResult(finalTranscript.trim());
      }
    };

    recognition.onerror = (event: any) => {
      const errorMessage = event.error === 'no-speech' 
        ? 'No speech detected. Please try again.' 
        : `Error: ${event.error}`;
      setError(errorMessage);
      onListeningChange(false);
    };

    recognition.onend = () => {
      onListeningChange(false);
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [onResult, onListeningChange]);

  const handleVoiceClick = () => {
    if (!recognitionRef.current) return;

    if (isActive) {
      recognitionRef.current.stop();
      onListeningChange(false);
    } else {
      recognitionRef.current.start();
    }
  };

  if (!isSupported) {
    return null;
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleVoiceClick}
        className={`p-1 md:p-2 rounded-full transition-all ${
          isActive 
            ? 'bg-red-50 text-red-600 hover:bg-red-100' 
            : 'text-gray-500 hover:text-primary-600'
        }`}
        title={isActive ? 'Stop listening' : 'Voice search'}
        aria-label="Voice search"
      >
        {isActive ? (
          <>
            {/* @ts-ignore */}
            <FaStop className="text-lg md:text-xl animate-pulse" />
          </>
        ) : (
          <>
            {/* @ts-ignore */}
            <FaMicrophone className="text-lg md:text-xl" />
          </>
        )}
      </button>

      {error && (
        <div className="absolute top-full right-0 mt-2 bg-red-50 text-red-700 px-3 py-2 rounded-lg text-xs md:text-sm whitespace-nowrap z-10 shadow-lg">
          {error}
        </div>
      )}

      {isActive && (
        <div className="absolute top-full right-0 mt-2 bg-blue-50 text-blue-700 px-3 py-2 rounded-lg text-xs md:text-sm whitespace-nowrap z-10 shadow-lg flex items-center">
          <span className="inline-block w-2 h-2 bg-blue-600 rounded-full mr-2 animate-pulse"></span>
          Listening...
        </div>
      )}
    </div>
  );
};

export default VoiceSearch;
